import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import Signout from './pages/Signout';
import { useAuth } from './context/AuthContext';
import { TopNavBar } from './components/TopNavBar';
import { RouteLoading } from './components/RouteLoading';
import { useDateRangeStore } from './stores/useDateRangeStore';
import { useAttributeCountStore } from './stores/useAttributeCountStore';
import { useDarkMode } from './hooks/useDarkMode';
import { Sparkles } from 'lucide-react';
import {
  TutorialWelcomeModal,
  TutorialTour,
  TutorialCombinedHighlight,
  TutorialFiltersBlueBox,
  useTutorialStore,
  getStepsForTutorial,
  AI_ASSISTANT_PANEL_CONTENT_STEP_IDS,
} from './components/tutorials';
import {
  GuidedFilterOnboardingModal,
  GuidedFilterPostApplyTour,
  GuidedFilterPostApplyCombinedHighlight,
} from './components/onboarding';
import { NODE_GRAPH_TUTORIAL_DEMO_PRODUCT_ID } from './components/tutorials/tutorialSteps';
import ProductCardModal from './components/ProductCardModal';
import { getProducts } from './services/pgService';
import { mergePgProductRowsForId } from './shared/mergePgProductRows';
import { toast } from 'sonner';
import { useNetworkChartStore } from './stores/useNetworkChartStore';
import { RecoveryCallbackRedirect } from './components/RecoveryCallbackRedirect';
import { RootRedirect } from './components/RootRedirect';
import { RecoveryLockedApp } from './components/RecoveryLockedApp';
import { PageLoading } from './components/PageLoading';
import { AuthenticatedMfaGate } from './components/auth/AuthenticatedMfaGate';
import { SecurityEnrollmentGate } from './components/auth/SecurityEnrollmentGate';
import { useRecoveryLocked } from './hooks/useRecoveryLocked';
import { useUserDataStore } from './stores/useUserDataStore';
import { useThemeStore } from './stores/useThemeStore';
import { isValidProductIdParam, storePendingProductId } from './utils/pendingDashboardDeepLink';

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ApiPage = lazy(() => import('./pages/ApiPage'));
const MyAccountPage = lazy(() => import('./pages/MyAccountPage'));
const ApiKeyPage = lazy(() => import('./pages/ApiKeyPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const IntegratorsPage = lazy(() => import('./pages/IntegratorsPage'));
const AssetsPage = lazy(() => import('./pages/AssetsPage'));
const OAuthConsentPage = lazy(() => import('./pages/OAuthConsentPage'));
const SetupGuidesPage = lazy(() => import('./pages/SetupGuidesPage'));
const AiAssistant = lazy(() => import('./components/AiAssistant'));

const PrivateRoute = ({ children }: { children: JSX.Element }) => {
    const { isAuthenticated, authLoading } = useAuth();
    const location = useLocation();
    
    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-muted-foreground">Loading...</div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        // Check if we're on the dashboard page with a report link
        if (location.pathname === '/dashboard') {
            const searchParams = new URLSearchParams(location.search);
            const productId = searchParams.get('product');
            if (isValidProductIdParam(productId)) {
                storePendingProductId(productId);
            }
            const reportLink = searchParams.get('report');
            if (reportLink) {
                console.log('Storing report link before redirect:', reportLink);
                localStorage.setItem('pendingReportLink', reportLink);
            }
        }
        return <Navigate to="/login" />;
    }
    
    return (
        <AuthenticatedMfaGate>
            <SecurityEnrollmentGate>{children}</SecurityEnrollmentGate>
        </AuthenticatedMfaGate>
    );
};

/** Consent stays reachable while logged out (stores pending path → login), but requires MFA when signed in. */
const OAuthConsentRoute = () => {
    const { isAuthenticated, authLoading } = useAuth();

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-muted-foreground">Loading...</div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <OAuthConsentPage />;
    }

    return (
        <AuthenticatedMfaGate>
            <SecurityEnrollmentGate>
                <OAuthConsentPage />
            </SecurityEnrollmentGate>
        </AuthenticatedMfaGate>
    );
};

const App: React.FC = () => {
    const recoveryLocked = useRecoveryLocked();
    const { authLoading } = useAuth();

    if (recoveryLocked) {
        return <RecoveryLockedApp />;
    }

    if (authLoading) {
        return <PageLoading />;
    }

    return <MainApp />;
};

const MainApp: React.FC = () => {
    const { isInitialized, setInitialized } = useDateRangeStore();
    const fetchAttributeCounts = useAttributeCountStore(state => state.fetchAttributeCounts);
    const { isAuthenticated } = useAuth();
    const location = useLocation();

    const {
        setWelcomeOpen,
        isRunning: isTutorialRunning,
        currentTutorialId,
        stepIndex: tutorialStepIndex,
        nodeGraphTutorialModalProduct,
        setNodeGraphTutorialModalProduct,
    } = useTutorialStore();
    const { userData, fetchUserData, isLoading: userDataLoading } = useUserDataStore();
    const tutorialSteps = currentTutorialId ? getStepsForTutorial(currentTutorialId) : [];
    const currentTutorialStepId = tutorialSteps[tutorialStepIndex]?.id;
    const expandAiPanelForTutorial =
        isTutorialRunning &&
        (currentTutorialId === 'ai-assistant' ||
            (currentTutorialStepId != null &&
                (AI_ASSISTANT_PANEL_CONTENT_STEP_IDS as readonly string[]).includes(currentTutorialStepId)));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isDarkMode } = useDarkMode();

    useEffect(() => {
      if (isAuthenticated) {
        void fetchUserData();
      }
    }, [isAuthenticated, fetchUserData]);

    useEffect(() => {
      useThemeStore.getState().syncPreference(userData.setTheme);
    }, [userData.setTheme]);

    useEffect(() => {
      if (
        isAuthenticated &&
        !userDataLoading &&
        userData.firstTimeLogin === true &&
        location.pathname === '/dashboard'
      ) {
        setWelcomeOpen(true);
      }
    }, [isAuthenticated, userDataLoading, userData.firstTimeLogin, location.pathname, setWelcomeOpen]);

    // Global right panel state
    const [isRightPanelExpanded, setIsRightPanelExpanded] = useState(() => {
        const saved = localStorage.getItem('workbenchRightPanelExpanded');
        return saved === null ? true : saved === 'true';
    });

    // Listen for right panel state changes
    useEffect(() => {
        const handleStorageChange = () => {
            const saved = localStorage.getItem('workbenchRightPanelExpanded');
            setIsRightPanelExpanded(saved === null ? true : saved === 'true');
        };

        window.addEventListener('localStorageChange', handleStorageChange);
        return () => window.removeEventListener('localStorageChange', handleStorageChange);
    }, []);

    useEffect(() => {
        if (!isInitialized) {
            setInitialized(true);
            //fetchAttributeCounts();
        }
    }, [isInitialized, setInitialized, fetchAttributeCounts]);

    // AI Assistant tutorial (and primary tour AI steps) target nodes inside the right panel; open it if collapsed.
    useEffect(() => {
        if (!expandAiPanelForTutorial) return;
        const saved = localStorage.getItem('workbenchRightPanelExpanded');
        const alreadyOpen = saved === null ? true : saved === 'true';
        if (alreadyOpen && isRightPanelExpanded) return;
        setIsRightPanelExpanded(true);
        localStorage.setItem('workbenchRightPanelExpanded', 'true');
        window.dispatchEvent(new Event('localStorageChange'));
    }, [expandAiPanelForTutorial, isRightPanelExpanded]);

    useEffect(() => {
        if (!isAuthenticated || !isTutorialRunning || currentTutorialId !== 'node-graph') {
            return;
        }
        if (nodeGraphTutorialModalProduct?.id === NODE_GRAPH_TUTORIAL_DEMO_PRODUCT_ID) {
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                // Request enough rows to include every getProducts row for this id (indicators are split across rows).
                const { products } = await getProducts([NODE_GRAPH_TUTORIAL_DEMO_PRODUCT_ID], 1, 500);
                if (cancelled) return;
                const merged = mergePgProductRowsForId(
                    products,
                    NODE_GRAPH_TUTORIAL_DEMO_PRODUCT_ID
                );
                if (!merged) {
                    toast.warning('Node Graph Overview', {
                        description: `Product ${NODE_GRAPH_TUTORIAL_DEMO_PRODUCT_ID} was not found in the database.`,
                    });
                    return;
                }
                setNodeGraphTutorialModalProduct(merged);
            } catch {
                if (!cancelled) {
                    toast.error('Could not load the demo product for Node Graph Overview.');
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [
        isAuthenticated,
        isTutorialRunning,
        currentTutorialId,
        nodeGraphTutorialModalProduct,
        setNodeGraphTutorialModalProduct,
    ]);

    return (
        <div className="h-screen flex flex-col">
            <RecoveryCallbackRedirect />
            {isAuthenticated && (
                <TopNavBar />
            )}
            <div className="flex flex-1 overflow-hidden">
                <main className="flex-1 overflow-auto bg-gradient-to-b from-background to-background/95 min-w-0">
                  <div className={`w-full pl-6 ${isRightPanelExpanded ? 'pr-0' : 'pr-6'}`}>
                    <Suspense fallback={<RouteLoading />}>
                    <Routes>
                        <Route path="/" element={<RootRedirect />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/oauth/consent" element={<OAuthConsentRoute />} />
                        <Route path="/reset-password" element={<Navigate to="/login" replace />} />
                        <Route path="/password-reset" element={<Navigate to="/reset-password" replace />} />
                        <Route path="/signout" element={<Signout />} />
                        <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
                        <Route path="/api" element={<PrivateRoute><ApiPage /></PrivateRoute>} />
                        <Route path="/my-account" element={<PrivateRoute><MyAccountPage /></PrivateRoute>} />
                        <Route path="/admin/setup-guides" element={<PrivateRoute><SetupGuidesPage /></PrivateRoute>} />
                        <Route path="/api-key" element={<PrivateRoute><ApiKeyPage /></PrivateRoute>} />
                        <Route path="/resources" element={<PrivateRoute><ResourcesPage /></PrivateRoute>} />
                        <Route path="/billing" element={<PrivateRoute><BillingPage /></PrivateRoute>} />
                        <Route path="/payment-success" element={<PrivateRoute><PaymentSuccessPage /></PrivateRoute>} />
                        <Route path="/integrators" element={<PrivateRoute><IntegratorsPage /></PrivateRoute>} />
                        <Route path="/assets" element={<PrivateRoute><AssetsPage /></PrivateRoute>} />
                        <Route path="/workbench" element={<Navigate to="/dashboard" replace />} />
                        <Route path="*" element={<Navigate to="/login" replace />} />
                    </Routes>
                    </Suspense>
                  </div>
                </main>

                {/* Global Right Panel */}
                {isAuthenticated && isRightPanelExpanded && (
                    <div className="w-[32rem] max-w-[36vw] shrink-0 border border-foreground/30 bg-background transition-all duration-300 ease-in-out">
                        <div className="h-full min-h-0 flex flex-col">
                            <div className="flex items-center gap-2 px-4 py-1 shrink-0">
                                <Sparkles className="h-4 w-4" />
                                <span className="text-sm">AI Assistant</span>
                            </div>
                            <div className="flex-1 min-h-0 p-2 flex flex-col">
                                <Suspense fallback={<RouteLoading />}>
                                    <AiAssistant />
                                </Suspense>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        {isAuthenticated && (
          <>
            {isTutorialRunning &&
              currentTutorialId === 'node-graph' &&
              tutorialStepIndex === 0 &&
              nodeGraphTutorialModalProduct && (
                <ProductCardModal
                  product={nodeGraphTutorialModalProduct}
                  isOpen
                  stackAboveDriverTutorial
                  tutorialVisualOnly
                  onClose={() => useTutorialStore.getState().endTutorial()}
                  onCreateNodeGraph={(id, p) => {
                    useNetworkChartStore.getState().onCreateNodeGraph?.(id, p);
                  }}
                />
              )}
            <TutorialWelcomeModal />
            <GuidedFilterOnboardingModal />
            <GuidedFilterPostApplyCombinedHighlight />
            <GuidedFilterPostApplyTour />
            <TutorialCombinedHighlight />
            <TutorialFiltersBlueBox />
            <TutorialTour />
          </>
        )}
        </div>
    );
};

export default App;